import React from 'react';
import { Box } from '@mui/material';
import { useContainerWidth } from '@/hooks/useContainerWidth';

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ByBp<T> = Partial<Record<Breakpoint, T>>;

const DEFAULT_BP = { sm: 600, md: 900, lg: 1200, xl: 1536 };

export const getContainerBp = (width: number, bp = DEFAULT_BP): Breakpoint => {
  if (width >= bp.xl) return 'xl';
  if (width >= bp.lg) return 'lg';
  if (width >= bp.md) return 'md';
  if (width >= bp.sm) return 'sm';

  return 'xs';
};

const pickByBreakpoint = <T,>(bp: Breakpoint, map: ByBp<T>): T | undefined => (
  map[bp] ?? (bp === 'xl' ? map.lg : undefined) ?? (bp === 'lg' ? map.md : undefined) ?? (bp === 'md' ? map.sm : undefined) ?? (bp === 'sm' ? map.xs : undefined)
);

type ColumnsByBp = Partial<Record<Breakpoint, number>>;

const GridBpContext = React.createContext<Breakpoint>('xs');

interface ContainerGridProps {
  children: React.ReactNode;
  gap?: number;
  columns?: ColumnsByBp;
  style?: React.CSSProperties;
};

export const ContainerGrid = (props: ContainerGridProps) => {
  const { children, gap = 16, columns = { xs: 1, sm: 2, md: 3, lg: 3, xl: 4 }, style } = props;
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const bp = getContainerBp(width);

  const columnsValue = pickByBreakpoint(bp, columns) ?? 1;

  return (
    <GridBpContext.Provider value={bp}>
      <Box
        ref={ref}
        style={style}
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columnsValue}, minmax(0, 1fr))`,
          gap: `${gap}px`,
          width: '100%',
        }}
      >
        {children}
      </Box>
    </GridBpContext.Provider>
  );
};

type SpanByBp = Partial<Record<Breakpoint, number>>;

interface ContainerGridItemProps {
  children?: React.ReactNode;
  span?: SpanByBp;
}

export const ContainerGridItem = (props: ContainerGridItemProps) => {
  const { children, span = { xs: 1 } } = props;
  const bp = React.useContext(GridBpContext);
  const spanValue = pickByBreakpoint(bp, span) ?? 1;

  return (
    <Box sx={{ gridColumn: `span ${spanValue}`, width: '100%' }}>
      {children}
    </Box>
  );
};
